/**
 * AI Workspace -- replaces Viewer area when open.
 * Renders tab-specific option panels (StyleGradeTab, TouchUpTab, ImportTab)
 * during the options step, and delegates to WorkspaceMain for generating/review steps.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkspaceMain } from './WorkspaceMain.tsx';
import { CreateTab } from './CreateTab.tsx';
import { StyleGradeTab } from './StyleGradeTab.tsx';
import { TouchUpTab } from './TouchUpTab.tsx';
import { ImportTab } from './ImportTab.tsx';
import { syncSelectionToTimelineOnClose } from './closeSelectionSync.ts';
import { GenerationHistoryPanel } from '@/components/GenerationHistoryPanel.tsx';
import { reapplyHistoryEntry } from '@/store/actions.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { useUiStore, type AiWorkspaceTab, type AiWorkspaceTask } from '@/store/uiStore.ts';
import { useAiWorkspaceFlow } from '@/hooks/useAiWorkspaceFlow.ts';
import { showToast } from '@/components/Toast.tsx';
import { trackEvent } from '@/telemetry/trackEvent.ts';
import type { GenerationHistoryEntry } from '@/types/generationHistory.ts';
import styles from './AiWorkspace.module.css';

const TABS: { id: AiWorkspaceTab; labelKey: string }[] = [
  { id: 'create', labelKey: 'tabCreate' },
  { id: 'stylegrade', labelKey: 'tabStyleGrade' },
  { id: 'touchup', labelKey: 'tabTouchUp' },
  { id: 'import', labelKey: 'tabImport' },
];

export function AiWorkspace() {
  const { t } = useTranslation('aiWorkspace');
  const flow = useAiWorkspaceFlow();
  const [historyOpen, setHistoryOpen] = useState(false);

  // Carry the selection across a tab change; also fire ai_panel_opened
  const activeTab = useUiStore(s => s.aiWorkspaceTab);
  const setActiveTab = useUiStore(s => s.setAiWorkspaceTab);
  const prevTabRef = useRef<AiWorkspaceTab | null>(null);
  useEffect(() => {
    const panelMap: Partial<Record<AiWorkspaceTab, 'image' | 'touchup' | 'create'>> = {
      create: 'create',
      stylegrade: 'image',
      touchup: 'touchup',
    };
    if (prevTabRef.current !== activeTab) {
      if (prevTabRef.current !== null) {
        // Tab changed — collapse the OUTGOING tab's effective shot into the shared selection
        // instead of wiping all three (2026-08-25). Wiping is why a shot picked on Touch Up did
        // not survive the walk over to Enhance or Import.
        flow.unifySelectionForTabSwitch(prevTabRef.current);
      }
      prevTabRef.current = activeTab;
      const panel = panelMap[activeTab];
      if (panel) trackEvent('ai_panel_opened', { panel });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /**
   * The ACTIVE tab's effective shot, mirrored during render for the close-path sync below.
   *
   * Assigned in render (the hostRef/generatedPromptRef idiom this feature area already uses) rather
   * than in an effect: the cleanup that reads it has empty deps and must see the LAST committed
   * value, not the value from whichever render last happened to re-run an effect.
   */
  const effectiveShotIdRef = useRef<number | null>(null);
  effectiveShotIdRef.current = (
    activeTab === 'create' ? flow.effectiveCreateShot?.id
      : activeTab === 'touchup' ? flow.effectiveTouchUpShot?.id
        : flow.currentShot?.id
  ) ?? null;

  /**
   * Close → hand the shot back to the Timeline.
   *
   * Every close route (the X, Esc, the gutter toggle, Alt+I/V, tabbing away, __requestBack, the
   * tutorial's finale) ends in this component unmounting, so one cleanup covers all of them —
   * which is also why this is here and not in uiStore.closeAiWorkspace: that action runs for the
   * board's Enhance dialog too, cannot see the flow's local selection, and is identity-pinned.
   *
   * Ordering against the flow's own unmount teardown does not matter: they write different stores.
   */
  useEffect(() => () => {
    syncSelectionToTimelineOnClose(effectiveShotIdRef.current);
  }, []);

  const handleHistoryPick = useCallback((entry: GenerationHistoryEntry) => {
    const shotId = useProjectStore.getState().selectedShotId;
    if (!shotId) {
      showToast(t('selectAShotFirst'), 'error');
      return;
    }

    if (entry.assetType === 'transition_video') {
      const ps = useProjectStore.getState();
      const project = ps.project;
      if (!project) return;
      let nextShotId: number | null = null;
      for (const scene of project.scenes) {
        const idx = scene.shots.findIndex(s => s.id === shotId);
        if (idx >= 0 && idx < scene.shots.length - 1) {
          nextShotId = scene.shots[idx + 1].id;
          break;
        }
      }
      if (!nextShotId) {
        showToast(t('noNextShotForContinuousTransition'), 'error');
        return;
      }
      const existing = ps.getTransition(shotId, nextShotId);
      if (!existing || existing.type !== 'continuous') {
        ps.setTransition(shotId, nextShotId, 'continuous');
      }
      ps.attachTransitionVideo(shotId, nextShotId, entry.objectUrl, entry.mimeType, entry.id);
    } else if (entry.assetType === 'video' || entry.assetType === 'image') {
      reapplyHistoryEntry(entry.id, shotId);
    } else {
      return;
    }

    setHistoryOpen(false);
    flow.closeWorkspace();
  }, [flow, t]);

  const isGenerating = flow.step === 'generating';

  const project = useProjectStore(s => s.project);
  const hasScreenplay = project?.hasScreenplay ?? false;

  // Get scene actions for the first grid-selected shot's scene
  const sceneActions = useMemo(() => {
    if (!project || !flow.currentShot) return [];
    const scene = project.scenes.find(s => s.shots.some(sh => sh.id === flow.currentShot!.id));
    if (!scene?.screenplay?.actions) return [];
    return scene.screenplay.actions;
  }, [project, flow.currentShot]); // currentShot is now grid-derived, not timeline

  const handleTaskChange = useCallback((newTask: AiWorkspaceTask) => {
    flow.handleTaskChange(newTask);
  }, [flow]);

  // Return to the options step (used by the review Close button). The selection is KEPT
  // (2026-08-25): a review the user closed out of says nothing about which shot they are working
  // on, and dropping it here would also strand the close-path sync with nothing to hand back.
  const handleReturnToOptions = useCallback(() => {
    flow.handleDiscard();
  }, [flow]);

  const closeAiWorkspace = useUiStore(s => s.closeAiWorkspace);
  const aiClosePendingAction = useUiStore(s => s.aiClosePendingAction);
  const confirmAiWorkspaceClose = useUiStore(s => s.confirmAiWorkspaceClose);
  const cancelAiWorkspaceClose = useUiStore(s => s.cancelAiWorkspaceClose);

  /**
   * "Add Shot" on a workspace the BOARD opened closes back to the board (20260925, owner item 15a).
   *
   * Scope is exactly the owner's case: a Create run with "Add as new shot" (the condition that
   * labels the button "Add Shot") in a workspace opened from the board (aiWorkspaceReturnToBoard).
   * A Timeline-originated workspace is a place you keep working in and stays open, and a `false`
   * Apply keeps the review live, as the board dialog does (BoardEnhanceDialog's Apply .then).
   *
   * The close runs one commit LATER, from an effect, never in the Apply's own continuation. The
   * unmount hand-back (effectiveShotIdRef → syncSelectionToTimelineOnClose) reads a ref assigned
   * during RENDER; a same-batch close would unmount without rendering finish()'s new selection and
   * hand back the ANCHOR instead of the new shot.
   */
  const [closeAfterApply, setCloseAfterApply] = useState(false);
  const handleApply = useCallback(() => {
    // Captured BEFORE the await: finish() nulls generationOrigin on the way out.
    const createdShot = flow.generationOrigin === 'create' && flow.createAddAsNewShot;
    void flow.handleApply().then((applied) => {
      if (applied && createdShot && useUiStore.getState().aiWorkspaceReturnToBoard) {
        setCloseAfterApply(true);
      }
    });
  }, [flow]);
  const lastAppliedShotIdRef = flow.lastAppliedShotIdRef;
  useEffect(() => {
    if (!closeAfterApply) return;
    const newShotId = lastAppliedShotIdRef.current;
    closeAiWorkspace();
    if (useUiStore.getState().aiWorkspaceOpen) {
      // The close was deferred (a generation still running → the confirm dialog). The workspace
      // stays up, so it must not stay blanked either.
      setCloseAfterApply(false);
      return;
    }
    // Belt-and-braces: the unmount hand-back skips a shot outside the Timeline's active scene
    // (closeSelectionSync's scoped-lane guard). The user is arriving at the BOARD, where that scope
    // does not apply, so select the new shot directly — AFTER the unmount, so the hand-back (which
    // also seeks the playhead) runs first and this only fills in when it skipped. Module state only:
    // the component is gone by the time this fires.
    if (newShotId != null) {
      setTimeout(() => {
        const ps = useProjectStore.getState();
        if (ps.selectedShotId === newShotId) return;
        const exists = ps.project?.scenes.some(sc => sc.shots.some(sh => sh.id === newShotId)) ?? false;
        if (exists) ps.selectShot(newShotId, true);
      }, 0);
    }
  }, [closeAfterApply, closeAiWorkspace, lastAppliedShotIdRef]);

  return (
    // Theme-aware since the 20260803 light-mode color audit (all chrome tokenized in
    // AiWorkspace.module.css) — the force-dark data-theme-scope stopgap is retired.
    <div className={styles.workspace}>
      <button className={styles.closeBtn} onClick={closeAiWorkspace} title={t('closeAiWorkspace')}>&#x24E7;</button>

      {/* Tab bar */}
      <div className={styles.tabBar}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
            /* Mid-generation the user stays where they started. This used to pin the exception to
               'stylegrade', which meant a Touch Up (or now a Create) run could be walked away from
               into Enhance while its own tab was locked — backwards. */
            disabled={isGenerating && tab.id !== activeTab}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* ── Options step: render tab-specific panels ──
          Not while an "Add Shot" close is pending: finish() lands the options step one commit
          before the close effect runs, and the Create tab would flash for that frame. */}
      {flow.step === 'options' && !closeAfterApply && activeTab === 'create' && (
        <CreateTab
          effectiveCreateShot={flow.effectiveCreateShot}
          setCreateTargetShotId={flow.setCreateTargetShotId}
          createPrompt={flow.createPrompt}
          setCreatePrompt={flow.setCreatePrompt}
          createTask={flow.createTask}
          setCreateTask={flow.setCreateTask}
          aiSelectedImageModel={flow.aiSelectedImageModel}
          aiSelectedVideoModel={flow.aiSelectedVideoModel}
          catalog={flow.catalog}
          setSelectedModel={flow.setSelectedModel}
          createAddAsNewShot={flow.createAddAsNewShot}
          setCreateAddAsNewShot={flow.setCreateAddAsNewShot}
          handleCreateGenerate={flow.handleCreateGenerate}
          closeWorkspace={flow.closeWorkspace}
        />
      )}

      {flow.step === 'options' && !closeAfterApply && activeTab === 'stylegrade' && (
        <StyleGradeTab
          task={flow.task}
          onTaskChange={handleTaskChange}
          continuousAvailable={flow.continuousAvailable}
          activeModelId={flow.activeModelId}
          catalog={flow.catalog}
          onSelectModel={flow.setSelectedModel}
          onOpenHistory={() => setHistoryOpen(true)}
          scope={flow.scope}
          onScopeChange={flow.handleScopeChange}
          overwriteExisting={flow.overwriteExisting}
          onToggleOverwrite={flow.setOverwriteExisting}
          showOverwriteOption={flow.hasStaticShotsInProject}
          skipApproved={flow.skipApproved}
          onToggleSkipApproved={flow.setSkipApproved}
          isEnhance={flow.isEnhance}
          scopeShots={flow.scopeShots}
          editablePrompt={flow.editablePrompt}
          onPromptChange={flow.setEditablePrompt}
          stylePreset={flow.stylePreset}
          onStylePresetChange={flow.handleStylePresetChange}
          editableMetadata={flow.editableMetadata}
          onMetadataChange={flow.setEditableMetadata}
          generateAudio={flow.generateAudio}
          onToggleAudio={flow.setGenerateAudio}
          retimeToMatchVideo={flow.retimeToMatchVideo}
          onToggleRetime={flow.toggleRetimeToMatchVideo}
          audioHint={flow.aiAudioHints[flow.effectiveSceneId_forAudio ?? -1] ?? ''}
          onAudioHintChange={hint => {
            if (flow.effectiveSceneId_forAudio != null) {
              flow.setAiAudioHint(flow.effectiveSceneId_forAudio, hint);
            }
          }}
          costEstimate={flow.costEstimate}
          estTotalSeconds={flow.estTotalSeconds}
          hasClamping={flow.hasClamping}
          durationLimits={flow.durationLimits}
          canRevert={flow.canRevert}
          canRestore={flow.canRestore}
          restoreLabel={flow.restoreLabel}
          restoreEntries={flow.restoreEntries}
          onRevert={flow.handleRevert}
          onRestore={flow.handleRestore}
          onGenerate={flow.handleGenerate}
          onClose={handleReturnToOptions}
          generateDisabled={flow.isEnhance ? flow.scopeShots.length === 0 : flow.scopeUnits.length === 0}
          selectedShotIds={flow.selectedShotIds}
          setSelectedShotIds={flow.setSelectedShotIds}
          toggleShotSelection={flow.toggleShotSelection}
          toggleSceneSelection={flow.toggleSceneSelection}
          onSetInitialPair={(pair) => useUiStore.setState({ aiWorkspaceInitialPair: pair })}
          autoScopeSwitch={flow.autoScopeSwitch}
          setAutoScopeSwitch={flow.setAutoScopeSwitch}
          onPromptReset={flow.handlePromptReset}
          hasScreenplay={hasScreenplay}
          sceneActions={sceneActions}
        />
      )}

      {flow.step === 'options' && !closeAfterApply && activeTab === 'touchup' && (
        <TouchUpTab
          effectiveTouchUpShot={flow.effectiveTouchUpShot}
          setTouchUpTargetShotId={flow.setTouchUpTargetShotId}
          touchUpPrompt={flow.touchUpPrompt}
          setTouchUpPrompt={flow.setTouchUpPrompt}
          aiSelectedTouchUpModel={flow.aiSelectedTouchUpModel}
          catalog={flow.catalog}
          setSelectedModel={flow.setSelectedModel}
          handleTouchUpGenerate={flow.handleTouchUpGenerate}
          canRevert={flow.canRevert}
          handleRevert={flow.handleRevert}
          closeWorkspace={flow.closeWorkspace}
        />
      )}

      {flow.step === 'options' && !closeAfterApply && activeTab === 'import' && (
        <ImportTab
          effectiveShot={flow.currentShot}
          onSelectShot={(shotId) => flow.setSelectedShotIds(new Set([shotId]))}
          onStartImportReview={(dataUrl) => {
            // Guarded in ImportTab too (that is where the "select a shot first" toast lives); this
            // is the type narrowing, and both read the same render's currentShot.
            if (flow.currentShot) flow.startImportReview(flow.currentShot, dataUrl);
          }}
        />
      )}

      {/* ── Generating / review steps: delegate to WorkspaceMain ── */}
      {flow.step !== 'options' && (
        <WorkspaceMain
          step={flow.step}
          task={flow.task}
          onTaskChange={flow.handleTaskChange}
          continuousAvailable={flow.continuousAvailable}
          activeModelId={flow.activeModelId}
          catalog={flow.catalog}
          onSelectModel={flow.setSelectedModel}
          isGenerating={isGenerating}
          onOpenHistory={() => setHistoryOpen(true)}
          scope={flow.scope}
          onScopeChange={flow.handleScopeChange}
          overwriteExisting={flow.overwriteExisting}
          onToggleOverwrite={flow.setOverwriteExisting}
          showOverwriteOption={flow.hasStaticShotsInProject}
          skipApproved={flow.skipApproved}
          onToggleSkipApproved={flow.setSkipApproved}
          isEnhance={flow.isEnhance}
          currentShot={flow.currentShot}
          scopeShots={flow.scopeShots}
          editablePrompt={flow.editablePrompt}
          onPromptChange={flow.setEditablePrompt}
          stylePreset={flow.stylePreset}
          onStylePresetChange={flow.handleStylePresetChange}
          generatedPrompt={flow.generatedPrompt}
          editableMetadata={flow.editableMetadata}
          onMetadataChange={flow.setEditableMetadata}
          generateAudio={flow.generateAudio}
          onToggleAudio={flow.setGenerateAudio}
          retimeToMatchVideo={flow.retimeToMatchVideo}
          onToggleRetime={flow.toggleRetimeToMatchVideo}
          audioHint={flow.aiAudioHints[flow.effectiveSceneId_forAudio ?? -1] ?? ''}
          onAudioHintChange={hint => {
            if (flow.effectiveSceneId_forAudio != null) {
              flow.setAiAudioHint(flow.effectiveSceneId_forAudio, hint);
            }
          }}
          scopeShotCount={flow.scopeShots.length}
          scopeUnitCount={flow.scopeUnits.length}
          costEstimate={flow.costEstimate}
          estTotalSeconds={flow.estTotalSeconds}
          hasClamping={flow.hasClamping}
          durationLimits={flow.durationLimits}
          progress={flow.progress}
          completedMedia={flow.completedMedia}
          progressItems={flow.progressItems}
          elapsedSeconds={flow.elapsedSeconds}
          reviewItems={flow.reviewItems}
          onToggleItem={flow.handleToggleItem}
          onAcceptAll={flow.handleAcceptAll}
          onRejectAll={flow.handleRejectAll}
          acceptedCount={flow.acceptedCount}
          onGenerate={flow.handleGenerate}
          onCancel={flow.handleCancel}
          onApply={handleApply}
          onDiscard={flow.handleDiscard}
          onClose={handleReturnToOptions}
          canRevert={flow.canRevert}
          canRestore={flow.canRestore}
          restoreLabel={flow.restoreLabel}
          restoreEntries={flow.restoreEntries}
          onRevert={flow.handleRevert}
          onRestore={flow.handleRestore}
          generateDisabled={flow.isEnhance ? flow.scopeShots.length === 0 : flow.scopeUnits.length === 0}
          previewShotId={flow.previewShotId}
          onPreviewShot={flow.setPreviewShotId}
          onRetry={flow.handleRetry}
          onPrioritize={flow.handlePrioritize}
          onTouchUpFromPreview={flow.handleTouchUpFromPreview}
          onPromptReset={flow.handlePromptReset}
          generationOrigin={flow.generationOrigin}
          createPrompt={flow.createPrompt}
          createAddAsNewShot={flow.createAddAsNewShot}
          applyBusy={flow.applyBusy}
          hasScreenplay={hasScreenplay}
          sceneActions={sceneActions}
        />
      )}

      {/* History panel overlay */}
      {historyOpen && (
        <GenerationHistoryPanel
          onPick={handleHistoryPick}
          onDismiss={() => setHistoryOpen(false)}
          initialFilter={flow.isEnhance ? 'image' : 'video'}
        />
      )}

      {/* Confirm dialog when navigating away during generation */}
      {aiClosePendingAction && (
        <div className={styles.confirmOverlay} onPointerDown={cancelAiWorkspaceClose}>
          <div className={styles.confirmDialog} onPointerDown={e => e.stopPropagation()}>
            <h3 className={styles.confirmTitle}>{t('generationInProgressTitle')}</h3>
            <p className={styles.confirmBody}>{t('generationInProgressBody')}</p>
            <div className={styles.confirmButtons}>
              <button className={styles.confirmNoBtn} onClick={cancelAiWorkspaceClose}>{t('generationInProgressNo')}</button>
              <button className={styles.confirmYesBtn} onClick={confirmAiWorkspaceClose}>{t('generationInProgressYes')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
